
import { useState } from "react";
import { FileProcessingService } from "../../services/files/fileProcessingService";
import { useToast } from "../use-toast";
import { uploadFileToSupabase } from "../../lib/supabase/storage";
import { useAuth } from "../../context/AuthContext";

export const useFileUpload = () => {
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const { toast } = useToast();
  const { user } = useAuth();

  const handleImageSelect = async (file: File) => {
    if (!FileProcessingService.validateFileType(file, ['image/'])) {
      toast({
        title: "Invalid file type",
        description: "Please select a valid image file",
        variant: "destructive"
      });
      return;
    }

    setSelectedImage(file);
    setIsUploading(true);
    
    // Create immediate preview URL
    const reader = new FileReader();
    reader.onload = (e) => {
      setImagePreview(e.target?.result as string);
    };
    reader.readAsDataURL(file);

    // Upload to Supabase Storage immediately (eager upload)
    try {
      console.log('🚀 EAGER-UPLOAD: Starting immediate upload for:', file.name);
      
      if (!user) {
        throw new Error('User not authenticated');
      }
      
      const uploadResult = await uploadFileToSupabase(file, user.id);
      
      if (uploadResult.success && uploadResult.url) {
        setImageUrl(uploadResult.url);
        console.log('✅ EAGER-UPLOAD: Upload successful:', uploadResult.url);
      } else {
        console.log('❌ EAGER-UPLOAD: Upload failed:', uploadResult.error);
        toast({
          title: "Upload failed",
          description: "Failed to upload image. You can still send it.",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('❌ EAGER-UPLOAD: Upload error:', error);
      toast({
        title: "Upload failed", 
        description: "Failed to upload image. You can still send it.",
        variant: "destructive"
      });
    } finally {
      setIsUploading(false);
    }
  };

  const processFiles = async () => {
    const files: { type: 'image' | 'document', content: string, filename: string }[] = [];

    if (selectedImage) {
      try {
        // Use the uploaded URL if available, otherwise fall back to base64
        const content = imageUrl || await FileProcessingService.compressAndConvertToBase64(selectedImage);
        
        console.log('📦 PROCESS-FILES: Using content type:', {
          filename: selectedImage.name,
          hasUploadedUrl: !!imageUrl,
          contentType: imageUrl ? 'url' : 'base64',
          contentLength: content.length
        });
        
        files.push({ type: 'image', content, filename: selectedImage.name });
      } catch (error) {
        toast({
          title: "Error processing image",
          description: "Failed to process the selected image",
          variant: "destructive"
        });
        throw error;
      }
    }

    return files;
  };

  const clearFiles = () => {
    setSelectedImage(null);
    setImagePreview(null);
    setImageUrl(null);
    setIsUploading(false);
  };

  return {
    selectedImage,
    imagePreview,
    imageUrl,
    isUploading,
    handleImageSelect,
    processFiles,
    clearFiles
  };
};
